import React from 'react';

import PropTypes from 'prop-types';

import { pluralize } from 'utils';

// Components
import { Mui } from 'components';
import { Label } from 'components/layouts';

import * as DisplayData from '../DisplayData';

const READY_HINT =
  'Tem tudo o que é obrigatório para vender. Para aparecer na loja, também precisa estar em pelo menos uma vitrine (aba Vendas do produto).';

export const describeActiveAllocations = summary =>
  `${summary.browsableAllocationsCount} de ${pluralize(
    summary.allocationsCount,
    'alocação ativa',
    'alocações ativas',
  )} na loja`;

export const getAllocationState = summary => {
  if (!summary) return null;

  if (!summary.isAllocated)
    return {
      color: 'warning',
      icon: 'storefront',
      label: 'Não alocado',
      hint: 'O produto não está em nenhuma vitrine.',
    };

  if (!summary.isBeingSold)
    return {
      color: 'warning',
      icon: 'visibility_off',
      label: 'Alocado, sem venda ativa',
      hint: `${describeActiveAllocations(summary)}, mas ninguém consegue comprar agora.`,
    };

  return {
    color: 'success',
    icon: 'sell',
    label: 'Sendo vendido agora',
    hint: `${describeActiveAllocations(summary)}.`,
  };
};

const getMissingRequirements = ({ checklist, primaryImage, productClass, hasStock }) => {
  if (checklist)
    return [
      checklist.price === false && 'preço',
      checklist.image === false && 'imagem',
      checklist.stock === false && 'estoque',
    ].filter(Boolean);

  return [
    !primaryImage?.file && 'imagem',
    productClass?.trackStock && !hasStock && 'estoque',
  ].filter(Boolean);
};

export const getSellingStatus = (product, allocationSummary) => {
  const { isActive, isAvailable, publishedDate } = product;

  if (!isActive)
    return {
      color: 'error',
      label: 'Inativo',
      hint: 'O produto está inativo e não aparece na loja.',
    };

  if (!isAvailable) {
    const missing = getMissingRequirements(product);

    return {
      color: 'warning',
      label: 'Com pendências',
      hint: missing.length
        ? `Falta ${missing.join(' e ')} para o produto ser vendido.`
        : 'Falta preço, imagem ou estoque para o produto ser vendido.',
    };
  }

  if (publishedDate && new Date(publishedDate) > new Date())
    return {
      color: 'info',
      label: 'Agendado',
      hint: (
        <>
          Começa a ser vendido em <DisplayData.DateField type="datetime" value={publishedDate} />
        </>
      ),
    };

  return (
    getAllocationState(allocationSummary) || {
      color: 'success',
      label: 'Pronto para venda',
      hint: READY_HINT,
    }
  );
};

function ProductSellingStatus({ product, allocationSummary, onClick }) {
  // Constants
  const { color, label, hint } = getSellingStatus(product, allocationSummary);
  const status = (
    <Label color={color} clickable={!!onClick}>
      {label}
    </Label>
  );

  return (
    <Mui.Tooltip title={hint}>
      <span>{onClick ? <Mui.ButtonBase onClick={onClick}>{status}</Mui.ButtonBase> : status}</span>
    </Mui.Tooltip>
  );
}

ProductSellingStatus.propTypes = {
  product: PropTypes.object.isRequired,
  allocationSummary: PropTypes.object,
  onClick: PropTypes.func,
};

export default ProductSellingStatus;
